import { AvatarRequester } from '@cobliteam/api-lib/src/account/avatar/AvatarRequester';
import useSessionStore from '@cobliteam/hooks/src/useSession/store/useSessionStore';
import { Environment } from '@cobliteam/react-environments';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();
const avatarRequester = new AvatarRequester({ host: env.herbieApiHost });

export const QUERY_KEY_USER_AVATAR = 'USER_AVATAR';

/**
 * A URL é derivada do id do usuário, ou seja, não muda entre uploads. Sem query string o
 * browser (e o CloudFront do cliente) continuam mostrando a foto antiga depois de trocar.
 */
const withCacheBust = (url: string) => `${url}?${Date.now()}`;

/**
 * Foto de perfil do usuário logado: leitura, upload e remoção.
 *
 * As mensagens de sucesso/erro ficam com o componente (que tem i18n); aqui só a query e o
 * cache. Chame `uploadAvatar(blob, { onSuccess, onError })` para plugar os alertas.
 */
const useUserAvatar = () => {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.session?.user.id);
  const queryKey = [QUERY_KEY_USER_AVATAR, userId];

  const { data, isLoading } = useQuery<string | null>({
    queryKey,
    queryFn: () => avatarRequester.getAvatarUrl(),
    enabled: Boolean(userId),
  });

  const uploadMutation = useMutation<string, unknown, Blob>({
    mutationFn: (blob) => avatarRequester.uploadAvatar(blob),
    onSuccess: (avatarUrl) => queryClient.setQueryData(queryKey, withCacheBust(avatarUrl)),
  });

  const removeMutation = useMutation<null, unknown, void>({
    mutationFn: () => avatarRequester.deleteAvatar(),
    onSuccess: () => queryClient.setQueryData(queryKey, null),
  });

  return {
    avatarUrl: data ?? null,
    isLoadingAvatar: isLoading,
    isSavingAvatar: uploadMutation.isPending || removeMutation.isPending,
    uploadAvatar: uploadMutation.mutate,
    removeAvatar: removeMutation.mutate,
  };
};

export default useUserAvatar;
